<template>
  <div class="flex flex-col w-full">
    <div
      class="flex aspect-square select-none items-center justify-center border-none mb-8"
      :class="{ 'rounded-full': sliderItem.imageRound }"
    >
      <span class="flex h-full w-full items-center justify-center">
        <VcImage
          :src="sliderItem.image"
          class="contain rounded-lg"
          :lazy="true"
          :alt="sliderItem.title"
          :class="{ 'aspect-square rounded-full': sliderItem.imageRound }"
        />
      </span>
    </div>

    <div class="flex-block text-lg font-semibold w-full mb-6">
      {{ sliderItem.title }}
    </div>

    <div class="text-base" :class="{ 'mb-6': hasTextLink }">
      <VcMarkdownRender :src="sliderItem.text" />
    </div>

    <div v-if="hasTextLink" class="text-base">
      <!-- Innovadis custom: sanitize slider external URL before binding to href -->
      <a
        v-if="isExternalLink"
        target="_blank"
        class="text-[--color-link] text-sm font-semibold"
        rel="noopener noreferrer"
        :href="getSafeUrl(sliderItem.linkUrl as string)"
      >
        {{ sliderItem.linkLabel }}
      </a>
      <router-link v-else :to="sliderItem.linkUrl || '#'">
        <span class="text-[--color-link] text-base font-semibold">{{ sliderItem.linkLabel }}</span>
      </router-link>
    </div>
  </div>
</template>
<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from "vue";
import type { IContentSliderLongTextsType } from "@/core/types/innovadis/inno-content-slider-item-long-texts";
import { getLinkAttr } from "@/core/utilities";
import { getSafeUrl } from "@/core/utilities/url/safeUrl";
interface IProps {
  sliderItem: IContentSliderLongTextsType;
}

const props = defineProps<IProps>();

const hasTextLink = computed(() => {
  const { linkLabel, linkUrl } = props.sliderItem;
  return !!linkLabel?.trim() && !!linkUrl?.trim();
});

const isExternalLink = computed(() => {
  return "externalLink" in getLinkAttr(props.sliderItem.linkUrl);
});

const textContainer = ref<HTMLElement | null>(null);

const applyDynamicClamp = () => {
  nextTick(() => {
    const el = textContainer.value;
    if (!el) return;

    const style = window.getComputedStyle(el);
    const lineHeight = parseFloat(style.lineHeight);
    const containerHeight = el.clientHeight;

    const lines = Math.floor(containerHeight / lineHeight);

    // Apply clamp
    el.style.webkitLineClamp = lines.toString();
  });
};

onMounted(() => {
  applyDynamicClamp();
  window.addEventListener("resize", applyDynamicClamp);
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", applyDynamicClamp);
});
</script>
